import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

// Nested scope chain diagram (boxes within boxes)
const ScopeChainDiagram: React.FC = () => (
 <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-6">
  <div className="rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 p-4">
   <div className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-500 mb-2">Global scope · <span className="font-mono text-amber-600 dark:text-amber-400">company</span></div>
   <div className="rounded-lg border-2 border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-900/15 p-4">
    <div className="text-xs font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">outer() · <span className="font-mono">team</span></div>
    <div className="rounded-lg border-2 border-primary-300 dark:border-primary-700 bg-primary-50/50 dark:bg-primary-900/15 p-4">
     <div className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400">inner()</div>
     <div className="mt-1 text-[12px] text-gray-600 dark:text-gray-300">can read <span className="font-mono">team</span> ↑ and <span className="font-mono">company</span> ↑↑</div>
    </div>
   </div>
  </div>
  <p className="mt-3 text-center text-xs text-gray-600 dark:text-gray-400">Inner scopes can read outward; outer scopes cannot read inward.</p>
 </div>
);

export const JSScope: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Scope &amp; Hoisting</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Where variables live and who can see them — plus how JavaScript prepares declarations before a single line runs. The concepts behind most variable bugs.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="15 min read" />
  </div>

  {/* 1. Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>On a large app, two developers both declare <Code>const taxRate</Code> with different values, and a third accidentally reassigns it. Which one wins? Who can even access it? And then there&apos;s this:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(userName);\nvar userName = "Sachin";` }]} defaultLanguage="javascript" id="scope-puzzle" />
   <Para>Why doesn&apos;t this crash? How can JavaScript reference a variable <Mark>before</Mark> it appears in the code? The answers are two concepts that separate junior from experienced developers: <Mark>scope</Mark> and <Mark>hoisting</Mark>.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200 mb-2"><Mark>Scope</Mark> determines where a variable can be accessed — the visibility rules of your program.</p>
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>Hoisting</Mark> is JavaScript moving declarations into memory before execution begins.</p>
   </div>
   <Callout tone="info" title="Key distinction">Declarations are hoisted; the execution order is <em>not</em> changed. This single point is the source of most confusion.</Callout>
  </section>

  {/* 3. Why scope exists */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Scope Exists</SectionHeading>
   <Para>If every variable were globally accessible, any file or function could overwrite it and quietly break business logic. Scope controls visibility so large apps stay maintainable.</Para>
   <div className="my-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
    {['Prevents accidental modification', 'Reduces bugs', 'Improves maintainability', 'Creates clear boundaries'].map((b) => (
     <div key={b} className="flex items-start gap-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-[12px] text-gray-600 dark:text-gray-300"><span className="text-green-500">✓</span>{b}</div>
    ))}
   </div>
  </section>

  {/* 4. Core idea */}
  <section className="mb-12">
   <SectionHeading n={4}>Core Idea</SectionHeading>
   <Para>Think of scope like rooms in a house. Things inside a room are easy to reach from that room — but not necessarily from another. A variable exists inside a specific scope, and only code inside that scope can access it.</Para>
  </section>

  {/* 5. Global scope */}
  <section className="mb-12">
   <SectionHeading n={5}>Global Scope</SectionHeading>
   <Para>Variables declared outside all functions and blocks are global — accessible everywhere:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const appName = "Food Delivery";\n\nfunction printName() {\n  console.log(appName); // reads the global\n}\n\nprintName();` }]} defaultLanguage="javascript" id="scope-global" />
   <Output>Food Delivery</Output>
  </section>

  {/* 6. Function scope */}
  <section className="mb-12">
   <SectionHeading n={6}>Function Scope</SectionHeading>
   <Para>A variable declared inside a function only exists inside that function:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createOrder() {\n  const orderId = 101;\n  console.log(orderId); // 101\n}\n\ncreateOrder();\nconsole.log(orderId); // ReferenceError — not visible out here` }]} defaultLanguage="javascript" id="scope-function" />
   <Callout tone="warning"><Code>orderId</Code> belongs only to <Code>createOrder()</Code>; outside code cannot see it.</Callout>
  </section>

  {/* 7. Block scope */}
  <section className="mb-12">
   <SectionHeading n={7}>Block Scope</SectionHeading>
   <Para>Introduced with <Code>let</Code> and <Code>const</Code>, a block is anything inside <Code>{`{ }`}</Code>:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `{\n  const city = "Delhi";\n}\n\nconsole.log(city); // ReferenceError — city only exists in the block` }]} defaultLanguage="javascript" id="scope-block" />
   <Para>This prevents accidental variable leakage out of <Code>if</Code> blocks, loops, and similar.</Para>
  </section>

  {/* 8. var vs let vs const */}
  <section className="mb-12">
   <SectionHeading n={8}>var vs let vs const Scope</SectionHeading>
   <Para>A classic interview topic — <Code>var</Code> is function-scoped and <Mark>ignores blocks</Mark>, while <Code>let</Code>/<Code>const</Code> respect them:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `if (true) {\n  var a = "Sachin";\n  let b = "Rahul";\n}\n\nconsole.log(a); // "Sachin" — var escaped the block!\nconsole.log(b); // ReferenceError — let stayed inside` }]} defaultLanguage="javascript" id="scope-var-let" />
   <div className="my-6 overflow-x-auto">
    <table className="w-full text-sm">
     <thead>
      <tr className="border-b border-gray-200 dark:border-gray-700">
       {['Keyword', 'Scope', 'Reassignable', 'Block-aware'].map((h) => <th key={h} className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-400">{h}</th>)}
      </tr>
     </thead>
     <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
      <tr><td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">var</td><td className="px-4 py-3 text-gray-700 dark:text-gray-300">Function</td><td className="px-4 py-3 text-gray-700 dark:text-gray-300">Yes</td><td className="px-4 py-3 text-red-600 dark:text-red-400">No</td></tr>
      <tr><td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">let</td><td className="px-4 py-3 text-gray-700 dark:text-gray-300">Block</td><td className="px-4 py-3 text-gray-700 dark:text-gray-300">Yes</td><td className="px-4 py-3 text-green-600 dark:text-green-400">Yes</td></tr>
      <tr><td className="px-4 py-3 font-mono font-bold text-amber-600 dark:text-amber-400">const</td><td className="px-4 py-3 text-gray-700 dark:text-gray-300">Block</td><td className="px-4 py-3 text-red-600 dark:text-red-400">No</td><td className="px-4 py-3 text-green-600 dark:text-green-400">Yes</td></tr>
     </tbody>
    </table>
   </div>
  </section>

  {/* 9. Lexical scope */}
  <section className="mb-12">
   <SectionHeading n={9}>Lexical Scope</SectionHeading>
   <Para>JavaScript uses <Mark>lexical scoping</Mark>: scope is determined by <em>where code is written</em>, not where it is called. Inner functions can read variables from the functions that enclose them.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const company = "OpenAI";\n\nfunction outer() {\n  function inner() {\n    console.log(company); // reaches up to the global\n  }\n  inner();\n}\n\nouter();` }]} defaultLanguage="javascript" id="scope-lexical" />
   <Output>OpenAI</Output>
  </section>

  {/* 10. Scope chain */}
  <section className="mb-12">
   <SectionHeading n={10}>The Scope Chain</SectionHeading>
   <Para>When a variable isn&apos;t found, JavaScript walks outward — current scope → parent → … → global — and throws a <Code>ReferenceError</Code> only if it&apos;s never found.</Para>
   <ScopeChainDiagram />
   <CodeBlock snippets={[{ language: 'javascript', code: `const company = "OpenAI";\n\nfunction outer() {\n  const team = "Platform";\n  function inner() {\n    console.log(company); // found 2 levels up\n    console.log(team);    // found 1 level up\n  }\n  inner();\n}\n\nouter();` }]} defaultLanguage="javascript" id="scope-chain" />
   <Output>{`OpenAI\nPlatform`}</Output>
  </section>

  {/* 11. Intro to hoisting */}
  <section className="mb-12">
   <SectionHeading n={11}>Introduction to Hoisting</SectionHeading>
   <Para>The classic puzzle that confuses every beginner:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(name);\nvar name = "Sachin";` }]} defaultLanguage="javascript" id="hoist-intro" />
   <Output>undefined</Output>
   <Para>Not a <Code>ReferenceError</Code> — because the declaration is hoisted. JavaScript effectively runs it like this:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `var name;            // declaration hoisted to the top\nconsole.log(name);   // undefined\nname = "Sachin";     // assignment stays in place` }]} defaultLanguage="javascript" id="hoist-internal" />
  </section>

  {/* 12. Hoisting with var */}
  <section className="mb-12">
   <SectionHeading n={12}>Hoisting with var</SectionHeading>
   <Para>Only the <Mark>declaration</Mark> moves up; the assignment stays where it is. That&apos;s why a <Code>var</Code> read before its line is <Code>undefined</Code> rather than an error.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(score); // undefined\nvar score = 100;` }]} defaultLanguage="javascript" id="hoist-var" />
  </section>

  {/* 13. Hoisting with let/const */}
  <section className="mb-12">
   <SectionHeading n={13}>Hoisting with let &amp; const</SectionHeading>
   <Para>A common myth says <Code>let</Code>/<Code>const</Code> aren&apos;t hoisted. They <Mark>are</Mark> hoisted — but you can&apos;t access them before their declaration line:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log(age);\nlet age = 25; // ReferenceError — not undefined` }]} defaultLanguage="javascript" id="hoist-let" />
   <Para>The reason is the <Mark>Temporal Dead Zone</Mark>.</Para>
  </section>

  {/* 14. TDZ */}
  <section className="mb-12">
   <SectionHeading n={14}>Temporal Dead Zone (TDZ)</SectionHeading>
   <Para>The TDZ is the window between a variable being <Mark>created</Mark> in memory and being <Mark>initialized</Mark> on its declaration line. During it, the variable exists but cannot be touched.</Para>
   <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-5">
    <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap text-[12px] font-medium">
     <span className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300">variable created</span>
     <span className="text-gray-400">→</span>
     <span className="px-3 py-1.5 rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300">TDZ — access throws</span>
     <span className="text-gray-400">→</span>
     <span className="px-3 py-1.5 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 font-mono">let age = 25</span>
     <span className="text-gray-400">→</span>
     <span className="px-3 py-1.5 rounded-md border border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300">safe access</span>
    </div>
   </div>
  </section>

  {/* 15. Function hoisting */}
  <section className="mb-12">
   <SectionHeading n={15}>Function Hoisting</SectionHeading>
   <Para>Function <em>declarations</em> are fully hoisted — body and all — so you can call them before they appear:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `greet(); // works!\n\nfunction greet() {\n  console.log("Hello");\n}` }]} defaultLanguage="javascript" id="hoist-fn" />
   <Output>Hello</Output>
  </section>

  {/* 16. Function expression hoisting */}
  <section className="mb-12">
   <SectionHeading n={16}>Function Expression Hoisting</SectionHeading>
   <Para>Function <em>expressions</em> behave differently — the variable is hoisted but sits in the TDZ:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `greet(); // ReferenceError\n\nconst greet = function () {\n  console.log("Hello");\n};` }]} defaultLanguage="javascript" id="hoist-fnexpr" />
   <Callout tone="info">Because <Code>greet</Code> is a <Code>const</Code>, it follows TDZ rules and can&apos;t be called before its line.</Callout>
  </section>

  {/* 17. Real world */}
  <section className="mb-12">
   <SectionHeading n={17}>Real-World Example</SectionHeading>
   <Para>A tax helper reading a value from its parent scope via the scope chain:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const taxRate = 18;\n\nfunction calculateTax(amount) {\n  return (amount * taxRate) / 100; // taxRate found in parent scope\n}\n\nconsole.log(calculateTax(1000));` }]} defaultLanguage="javascript" id="scope-realworld" />
   <Output>180</Output>
  </section>

  {/* 18. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={18}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Using var in modern code">Prefer <Code>let</Code>/<Code>const</Code> — they respect block scope and avoid surprises.</Callout>
   <Callout tone="warning" title="Confusing block and function scope"><Code>var</Code> inside an <Code>if</Code> leaks out; many expect a <Code>ReferenceError</Code> but get the value.</Callout>
   <Callout tone="warning" title="Believing let isn't hoisted">It is hoisted — just protected by the TDZ.</Callout>
   <Callout tone="warning" title="Accessing before initialization"><Code>console.log(user)</Code> before <Code>const user = { }</Code> throws a <Code>ReferenceError</Code>.</Callout>
  </section>

  {/* 19. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={19}>Interview Concepts</SectionHeading>
   <div className="flex flex-wrap gap-2">
    {['What is scope?', 'Lexical scope', 'Scope chain', 'Global vs function scope', 'var vs let vs const', 'What is hoisting?', 'Temporal Dead Zone', 'Why undefined vs ReferenceError'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 20. Mental model */}
  <section className="mb-12">
   <SectionHeading n={20}>Mental Model</SectionHeading>
   <Para><Mark>Scope</Mark> is access control — teams reach their own resources, some are company-wide. <Mark>Hoisting</Mark> is preparation before work starts — the office opens and desks are set up (declarations placed in memory) before employees arrive (code executes).</Para>
  </section>

  {/* 21 & 22 */}
  <section className="mb-4">
   <SectionHeading n={21}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Scope determines where variables are accessible, while hoisting is JavaScript&apos;s behavior of preparing declarations in memory before code execution begins.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Scope controls visibility; hoisting controls initialization behavior. Together they explain how JavaScript finds variables, manages memory, and executes code — and most variable bugs come from misunderstanding scope, hoisting, or the TDZ.
   </Callout>
  </section>
 </div>
);

export default JSScope;
